import { supabase } from '../lib/supabase';
import { notificationService } from './notificationService';

/**
 * Error-surfacing pattern: see the canonical doc-comment at the top of
 * src/services/clientService.js. Services preserve the real error object on
 * `{ data, error }` (no `{ message }` stripping); page-layer callers branch
 * on `error?.code === 'PGRST301'` for RLS copy and otherwise surface the
 * real `error.message` alongside any friendly intro text.
 */
function surfaceError(scope, error) {
  console.error(`[ramsService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const ramsService = {
  /**
   * Upload a RAMS document file to Supabase Storage and create the DB record.
   */
  async uploadRams(title, siteId, file, uploadedBy, companyId) {
    try {
      const fileName = `${companyId}/${siteId}/${Date.now()}_${file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_')}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('rams-documents')
        ?.upload(fileName, file, { upsert: false });

      if (uploadError) {
        // Translate Supabase storage 413 to an actionable message. statusCode
        // is the string status; message-substring is a fallback for shapes
        // that don't carry it.
        const statusCode = String(uploadError?.statusCode ?? '');
        const msgStr = String(uploadError?.message ?? '');
        if (statusCode === '413' || /exceeded.*size|too large|payload too large/i.test(msgStr)) {
          const friendly = new Error('File is too large for the rams-documents bucket.');
          friendly.code = 'PAYLOAD_TOO_LARGE';
          friendly.statusCode = 413;
          friendly.cause = uploadError;
          throw friendly;
        }
        throw uploadError;
      }

      const filePath = uploadData?.path;

      const { data, error } = await supabase
        ?.from('rams_documents')
        ?.insert({
          company_id: companyId,
          site_id: siteId,
          title,
          file_path: filePath,
          uploaded_by: uploadedBy,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('uploadRams', error) };
    }
  },

  /**
   * Assign a RAMS document to multiple workers.
   */
  async assignRamsToWorkers(ramsId, workerUserIds) {
    try {
      if (!workerUserIds?.length) return { data: [], error: null };

      const rows = workerUserIds?.map((wid) => ({
        rams_document_id: ramsId,
        worker_user_id: wid,
      }));

      const { data, error } = await supabase
        ?.from('rams_assignments')
        ?.upsert(rows, { onConflict: 'rams_document_id,worker_user_id' })
        ?.select();

      if (error) throw error;

      // Notification fan-out is non-fatal — already happens fire-and-forget
      // and we've returned the assignment data above. A failure here doesn't
      // unwind the upsert.
      const { data: ramsDoc } = await supabase?.from('rams_documents')?.select('title')?.eq('id', ramsId)?.maybeSingle();
      const title = ramsDoc?.title || 'H&S Document';
      for (const wid of workerUserIds) {
        notificationService?.notifyRamsAssigned(ramsId, wid, title);
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('assignRamsToWorkers', error) };
    }
  },

  /**
   * Worker acknowledges a RAMS document.
   */
  async acknowledgeRams(ramsId, workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('rams_acknowledgements')
        ?.upsert(
          { rams_document_id: ramsId, worker_user_id: workerUserId },
          { onConflict: 'rams_document_id,worker_user_id' }
        )
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('acknowledgeRams', error) };
    }
  },

  /**
   * NSherjFh PR 3 — recoverable soft-delete on the H&S doc.
   *
   * Mirrors PR 2's `documentService.deleteDocument` exactly:
   *   - Stamps deleted_at + deleted_by on the row.
   *   - Leaves the storage file in `rams-documents` intact so restore
   *     surfaces both row and file (verify-end-to-end: download a
   *     restored doc).
   *   - Idempotent via `.is('deleted_at', null)`: a second click is a
   *     no-op (returns null data, no error) and preserves the original
   *     deleter attribution.
   *   - Existing `rams_assignments` and `rams_acknowledgements` rows
   *     are NOT deleted — they're historical compliance audit trail.
   *     The active rams-management tracking grid filters them out via
   *     getAllAssignmentsForCompany's parent-doc filter; per-worker
   *     ack history (`getWorkerAckStatus`, `getAcknowledgements`)
   *     intentionally does not filter, so audits stay complete.
   */
  async deleteRamsDocument(ramsId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('rams_documents')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', ramsId)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('deleteRamsDocument', error) };
    }
  },

  /**
   * Inverse of deleteRamsDocument. Clears deleted_at + deleted_by so the
   * doc reappears in every active surface (rams-management, site-detail
   * RAMS tab, worker ack queue, client portal, compliance roll-up).
   * Existing assignments + acknowledgements were never touched, so they
   * just become visible again.
   */
  async restoreRamsDocument(ramsId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('rams_documents')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', ramsId)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('restoreRamsDocument', error) };
    }
  },

  /**
   * Get all RAMS documents for a site (admin/site_manager).
   *
   * Default-filters soft-deleted H&S docs (NSherjFh PR 3). Pass
   * `{ includeDeleted: true }` from the rams-management "View deleted"
   * tab to surface them — the page then narrows to only the deleted
   * rows for display. Mirrors PR 2's `documentService.getDocumentsBy
   * Site` shape (and PR 1's `siteService.getSitesByCompany`).
   */
  async getRamsForSite(siteId, companyId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('rams_documents')
        ?.select('*, uploader:uploaded_by(id, full_name)')
        ?.eq('site_id', siteId)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getRamsForSite', error) };
    }
  },

  /**
   * Get all RAMS documents for a company (admin view).
   *
   * Same `includeDeleted` opt-in as getRamsForSite. Rams-management's
   * Deleted tab passes `true`; everything else gets the default-filtered
   * active set.
   */
  async getRamsForCompany(companyId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('rams_documents')
        ?.select('*, site:site_id(id, site_name), uploader:uploaded_by(id, full_name)')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getRamsForCompany', error) };
    }
  },

  /**
   * Get all RAMS assigned to a worker.
   */
  async getRamsForWorker(workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('rams_assignments')
        // NSherjFh PR 3: pull `deleted_at` on the embedded rams_document so
        // we can drop assignments to soft-deleted parents below. No point
        // surfacing a removed H&S doc to a worker's ack queue. The
        // assignment + any past acknowledgement rows stay in the DB as
        // historical fact; we just hide them from the active view.
        ?.select('*, rams_document:rams_document_id(id, title, file_path, created_at, site_id, deleted_at)')
        ?.eq('worker_user_id', workerUserId)
        ?.order('assigned_at', { ascending: false });

      if (error) throw error;

      const assignments = (data || [])?.filter((a) => a?.rams_document && !a?.rams_document?.deleted_at);

      // Collect unique site_ids to fetch site names separately (avoids nested join alias bug)
      const siteIds = [...new Set(assignments?.map((a) => a?.rams_document?.site_id)?.filter(Boolean))];
      let siteMap = {};
      if (siteIds?.length) {
        const { data: sites, error: sitesError } = await supabase
          ?.from('sites')
          ?.select('id, site_name')
          ?.in('id', siteIds);
        if (sitesError) throw sitesError;
        (sites || [])?.forEach((s) => { siteMap[s?.id] = s; });
      }

      const enriched = assignments?.map((a) => ({
        ...a,
        rams_document: a?.rams_document
          ? {
              ...a?.rams_document,
              site: siteMap?.[a?.rams_document?.site_id] || null,
            }
          : null,
      }));

      return { data: enriched, error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getRamsForWorker', error) };
    }
  },

  /**
   * Get all acknowledgements for a RAMS document.
   */
  async getAcknowledgements(ramsId) {
    try {
      const { data, error } = await supabase
        ?.from('rams_acknowledgements')
        ?.select('*, worker:worker_user_id(id, full_name)')
        ?.eq('rams_document_id', ramsId)
        ?.order('acknowledged_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAcknowledgements', error) };
    }
  },

  /**
   * Get all assignments for a RAMS document with acknowledgement status.
   */
  async getAssignmentsWithStatus(ramsId) {
    try {
      const { data: assignments, error: aErr } = await supabase
        ?.from('rams_assignments')
        ?.select('*, worker:worker_user_id(id, full_name)')
        ?.eq('rams_document_id', ramsId);

      if (aErr) throw aErr;

      const { data: acks, error: ackErr } = await supabase
        ?.from('rams_acknowledgements')
        ?.select('worker_user_id, acknowledged_at')
        ?.eq('rams_document_id', ramsId);

      if (ackErr) throw ackErr;

      const ackMap = {};
      acks?.forEach((a) => { ackMap[a?.worker_user_id] = a?.acknowledged_at; });

      const merged = assignments?.map((a) => ({
        ...a,
        acknowledged: !!ackMap?.[a?.worker_user_id],
        acknowledged_at: ackMap?.[a?.worker_user_id] || null,
      }));

      return { data: merged || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAssignmentsWithStatus', error) };
    }
  },

  /**
   * Get all assignments+ack status for all RAMS in a company (for the tracking table).
   */
  async getAllAssignmentsForCompany(companyId) {
    try {
      const { data: docs, error: docsErr } = await supabase
        ?.from('rams_documents')
        ?.select('id, title, site:site_id(id, site_name)')
        ?.eq('company_id', companyId)
        // NSherjFh PR 3: the acknowledgement tracking table on rams-management
        // is an active view — assignments to soft-deleted docs are hidden
        // from the live grid (their rows stay in rams_assignments and
        // rams_acknowledgements as historical fact, but the parent doc is
        // gated out here so the grid + the stats roll-up don't count them).
        ?.is('deleted_at', null);

      if (docsErr) throw docsErr;
      if (!docs?.length) return { data: [], error: null };

      const docIds = docs?.map((d) => d?.id);

      const { data: assignments, error: aErr } = await supabase
        ?.from('rams_assignments')
        ?.select('*, worker:worker_user_id(id, full_name)')
        ?.in('rams_document_id', docIds);

      if (aErr) throw aErr;

      const { data: acks, error: ackErr } = await supabase
        ?.from('rams_acknowledgements')
        ?.select('rams_document_id, worker_user_id, acknowledged_at')
        ?.in('rams_document_id', docIds);

      if (ackErr) throw ackErr;

      const ackMap = {};
      acks?.forEach((a) => {
        ackMap[`${a?.rams_document_id}_${a?.worker_user_id}`] = a?.acknowledged_at;
      });

      const docMap = {};
      docs?.forEach((d) => { docMap[d?.id] = d; });

      const merged = assignments?.map((a) => ({
        ...a,
        rams_document: docMap?.[a?.rams_document_id],
        acknowledged: !!ackMap?.[`${a?.rams_document_id}_${a?.worker_user_id}`],
        acknowledged_at: ackMap?.[`${a?.rams_document_id}_${a?.worker_user_id}`] || null,
      }));

      return { data: merged || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAllAssignmentsForCompany', error) };
    }
  },

  /**
   * Get a signed URL for a RAMS document file.
   *
   * Workers' assigned RAMS docs are uploaded as .docx in production today,
   * which browsers can't render inline — `window.open` on the bare signed
   * URL produces a blank/flickering tab. Forcing `Content-Disposition:
   * attachment` via the `download` option makes Storage emit a download
   * response for any MIME, so the open-in-new-tab path produces a clean
   * download instead of a non-rendering tab. PDFs go the same path —
   * consistent UX for workers, who are mostly on phones where inline
   * preview is unreliable anyway. Pass `downloadFilename` to suggest a
   * human filename (e.g. the RAMS doc title); falls back to `download: true`
   * which lets Storage pick the filename from the path.
   */
  async getSignedUrl(filePath, downloadFilename) {
    try {
      if (!filePath) {
        const err = new Error('No file path provided');
        err.code = 'NO_FILE_PATH';
        throw err;
      }
      const options = downloadFilename
        ? { download: downloadFilename }
        : { download: true };
      const { data, error } = await supabase?.storage
        ?.from('rams-documents')
        ?.createSignedUrl(filePath, 3600, options);

      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: surfaceError('getSignedUrl', error) };
    }
  },

  /**
   * Get workers assigned to a site (for the assignment panel).
   *
   * Two-step fetch. `worker_site_assignments` stores the worker via
   * `user_id` (not `worker_user_id` as the old embed assumed), so doing
   * an assignment-embed pattern silently errored and returned []. Fetch
   * the assignment ids first, then look up the profiles — avoids any
   * PostgREST relationship-embed surprise and surfaces real errors.
   */
  async getWorkersForSite(siteId) {
    try {
      const { data: assignments, error: assignError } = await supabase
        ?.from('worker_site_assignments')
        ?.select('user_id')
        ?.eq('site_id', siteId);
      if (assignError) throw assignError;
      const userIds = (assignments || [])?.map((a) => a?.user_id)?.filter(Boolean);
      if (!userIds?.length) return { data: [], error: null };
      const { data: profiles, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name')
        ?.in('id', userIds);
      if (profileError) throw profileError;
      return { data: profiles || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getWorkersForSite', error) };
    }
  },

  /**
   * Get worker acknowledgement status for a specific RAMS doc.
   */
  async getWorkerAckStatus(ramsId, workerUserId) {
    try {
      const { data, error } = await supabase
        ?.from('rams_acknowledgements')
        ?.select('id, acknowledged_at')
        ?.eq('rams_document_id', ramsId)
        ?.eq('worker_user_id', workerUserId)
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('getWorkerAckStatus', error) };
    }
  },
};
